import type { GameAnalyticsSchema } from '../../../analytics.config';
import type {
  EventName,
  TrackFunction,
  SetUserPropertiesFunction,
} from '@play-co/analytics-schema';
import { analytics } from '@play-co/gcinstant';

/**
 * Type safe track an event for Amplitude.
 *
 * @param eventName - The name of the event to track.
 * @param eventProperties - An object containing properties of the event.
 *
 * @example
 * // 1. Add your new event to the analytics.config.ts file:
 * export default A.createSchema('Buruburu', {
 *  events: {
 *    DialogOpen: A.event('Sent when the player opens a dialog', {
 *      id: A.string('The ID of the dialog'),
 *    }),
 *  },
 * });
 *
 * // 2. Use the track function to track the event:
 * track('DialogOpen', { id: 'dialog-id' });
 *
 */
export const track: TrackFunction<GameAnalyticsSchema> = (
  eventName,
  eventProperties,
) => {
  analytics.pushEvent(eventName, eventProperties);
};

/**
 * Tracks an event only with LINE Conversion API.
 *
 * Use this for LINE Ads conversion signals that should be sent separately from
 * the normal Amplitude tracking call.
 */
export function trackLineConversionApi(
  eventName: EventName<GameAnalyticsSchema>,
): void {
  const lineConversionApiService = analytics.services.lineConversionApi;

  if (!lineConversionApiService) return;

  analytics.pushEvent(eventName, {}, [lineConversionApiService]);
}

/**
 * Types safe function to set user properties on Amplitude
 *
 * @param userProperties - The properties to set for the user on Amplitude
 *
 * @example
 * // 1. Add new user property to the analytics.config.ts file:
 * export default A.createSchema('Buruburu', {
 *   events: {
 *    ...
 *   },
 *   userProperties: {
 *     propName: A.number('Property description'),
 *   },
 * });
 *
 * // 2. Use the setUserProperties function to set the user properties:
 * setUserProperties({
 *   propName: 1,
 * });
 */
export const setUserProperties: SetUserPropertiesFunction<
  GameAnalyticsSchema
> = (userProperties) => {
  analytics.setUserProperties(userProperties);
};
